// =========================================================================
// PeptideClients-managed Sendblue subaccounts (operator-facing view).
//
// Wraps reads on the lifecycle state machine in
// `core.sendblue_subaccounts` (migration
// `20260612000000_sendblue_managed_subaccounts.sql`). The write surface
// is owned by the `stripe-billing-webhook` (auto-provision on first
// paid status) and `sendblue-subaccount-worker` (lease + finalize +
// cancel); the operator app is read-only here aside from the per-org
// `messaging_line_preference` toggle (which lives in `orgSettings`)
// and an optional `requested_area_code` hint (which the worker uses
// on the first provisioning attempt).
//
// Types are declared inline rather than re-using `Database['core']
// ['Tables']['sendblue_subaccounts']` so this service compiles even
// before `pnpm db:types` is re-run against the new migration.
//
// See `apps/web/src/settings/SettingsMessagingPage.tsx` for the UI.
// =========================================================================

import { supabase } from '../client';

// ---- types --------------------------------------------------------------

export type SendblueSubaccountStatus =
  | 'queued'
  | 'provisioning'
  | 'active'
  | 'pending_cancel'
  | 'cancelled'
  | 'failed';

export type SendblueCancelReason =
  | 'subscription_canceled'
  | 'past_due_7d'
  | 'admin_action'
  | 'operator_request';

export interface SendblueSubaccount {
  id: string;
  org_id: string;
  status: SendblueSubaccountStatus;
  requested_area_code: string | null;
  sendblue_company_name: string | null;
  sendblue_assigned_number: string | null;
  vault_secret_id: string | null;
  connection_id: string | null;
  area_code_ticket_id: string | null;
  last_error: string | null;
  retry_count: number;
  cancel_reason: SendblueCancelReason | null;
  provisioned_at: string | null;
  cancel_requested_at: string | null;
  cancel_email_sent_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  // Phase-2 columns (migration 20260702000000_messaging_lines_and_platform_pool):
  // multiple managed lines per org, optional store assignment, default flag.
  label: string | null;
  assigned_store_id: string | null;
  is_default: boolean;
}

export interface SendblueSubaccountEvent {
  id: string;
  subaccount_id: string;
  org_id: string;
  event_kind:
    | 'provision_requested'
    | 'provision_leased'
    | 'provision_succeeded'
    | 'provision_failed'
    | 'cancel_requested'
    | 'cancel_email_sent'
    | 'cancelled';
  payload: Record<string, unknown>;
  at: string;
}

/**
 * Joined view used by apps/admin/src/pages/SendblueSubaccountsPage.tsx.
 * Returned by `core.rpc_admin_list_sendblue_subaccounts()` -- includes
 * org name/slug + subscription status + past_due_days so the table can
 * surface the orgs the sweeper is about to release on its next pass.
 */
export interface AdminSendblueSubaccountRow extends SendblueSubaccount {
  org_name: string;
  org_slug: string;
  subscription_status:
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'unpaid'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired'
    | null;
  past_due_since: string | null;
  past_due_days: number | null;
}

// ---- reads --------------------------------------------------------------

/**
 * Look up the org's DEFAULT managed Sendblue subaccount — the singleton
 * line every existing call site has been built around. Returns `null` when
 * the org has never had one (e.g. still on free trial, or the messaging
 * module was never enabled).
 *
 * With the Phase-2 messaging-lines pivot the org can own multiple
 * subaccounts; this helper now filters on `is_default = true` so legacy
 * callers (e.g. the original LineStatusCard rendering) keep their
 * singleton semantics. New surfaces should use {@link listOrgManagedLines}
 * instead so they see every dedicated line at once.
 *
 * Read access is gated by RLS:
 *  - platform admin: all rows
 *  - workspace member: rows for orgs they belong to
 *  - everyone else: zero rows (returned as null by maybeSingle)
 */
export async function getSendblueSubaccountForOrg(
  orgId: string,
): Promise<SendblueSubaccount | null> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('sendblue_subaccounts' as any)
    .select('*')
    .eq('org_id', orgId)
    .eq('is_default', true)
    .maybeSingle();
  if (error) throw error;
  return (data as SendblueSubaccount | null) ?? null;
}

/**
 * List every managed line (default + dedicated) for the given org, with
 * the org's chosen default first then ordered by creation time ascending.
 * Drives the per-line table on the operator's Settings → Messaging page
 * after the Phase-2 pivot.
 *
 * Backed by `core.rpc_list_org_managed_lines(p_org_id)` which enforces the
 * "member of org or platform admin" gate server-side. The wire shape
 * intentionally omits noisy provisioning columns (vault_secret_id,
 * connection_id, cancel_email_sent_at, …) so the operator UI only sees
 * what it actually renders.
 */
export interface OrgManagedLine {
  id: string;
  org_id: string;
  status: SendblueSubaccountStatus;
  is_default: boolean;
  label: string | null;
  sendblue_assigned_number: string | null;
  sendblue_company_name: string | null;
  assigned_store_id: string | null;
  assigned_store_name: string | null;
  provisioned_at: string | null;
  cancel_reason: SendblueCancelReason | null;
  last_error: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export async function listOrgManagedLines(orgId: string): Promise<OrgManagedLine[]> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_list_org_managed_lines' as any, { p_org_id: orgId });
  if (error) throw error;
  return (data ?? []) as unknown as OrgManagedLine[];
}

/**
 * Read the most recent sticky platform-pool assignments for the active
 * org (debug aid in Settings → Messaging "Shared pool" section). Returns
 * up to `limit` rows ordered last_used_at desc. RLS gate
 * `org_platform_assignments_member_read` constrains the result set to
 * orgs the caller belongs to.
 *
 * Caller passes `joinNumber=true` when it also wants to show the
 * `phone_e164` of the pool number assigned to each recipient.
 */
export interface PlatformStickyAssignment {
  org_id: string;
  recipient_phone_e164: string;
  platform_number_id: string;
  platform_phone_e164: string | null;
  first_used_at: string;
  last_used_at: string;
  message_count: number;
}

export async function listRecentPlatformStickyAssignments(
  orgId: string,
  limit = 5,
): Promise<PlatformStickyAssignment[]> {
  const capped = Math.max(1, Math.min(50, Math.floor(limit)));
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('org_platform_messaging_assignments' as any)
    .select(
      'org_id, recipient_phone_e164, platform_number_id, first_used_at, last_used_at, message_count, platform_messaging_numbers(phone_e164)',
    )
    .eq('org_id', orgId)
    .order('last_used_at', { ascending: false })
    .limit(capped);
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data ?? []) as any[]).map((row) => ({
    org_id: row.org_id,
    recipient_phone_e164: row.recipient_phone_e164,
    platform_number_id: row.platform_number_id,
    platform_phone_e164: row.platform_messaging_numbers?.phone_e164 ?? null,
    first_used_at: row.first_used_at,
    last_used_at: row.last_used_at,
    message_count: row.message_count ?? 0,
  }));
}

// ---- operator writes ----------------------------------------------------

/**
 * Purchase + provision a NEW dedicated SendBlue line for the given org via
 * the `stripe-billing-checkout` Edge Function. The function refuses with
 * SQLSTATE `PC003` while the org subscription is still on trial; the
 * `addDedicatedLine` call surfaces that as an Error whose message starts
 * with "trial_block" so the UI can render an upsell modal.
 *
 * Side effects (all driven server-side):
 *   1. `core.sendblue_subaccounts` row enqueued with `is_default=false`.
 *   2. Stripe subscription mutated: either a new line item with
 *      `price=messaging.dedicated_line, quantity=1`, or quantity +1 on
 *      the existing item. `payment_behavior=error_if_incomplete` so the
 *      operator's card is charged immediately at proration.
 *   3. Local `billing.org_subscription_items` mirror upserted so the next
 *      billing snapshot fetch reflects the new line.
 */
export interface AddDedicatedLineInput {
  orgId: string;
  label?: string | null;
  requestedAreaCode?: string | null;
  assignedStoreId?: string | null;
}

export interface AddDedicatedLineResult {
  ok: true;
  subaccount_id: string;
  subscription_id: string;
}

export async function addDedicatedLine(
  input: AddDedicatedLineInput,
): Promise<AddDedicatedLineResult> {
  const { data, error } = await supabase().functions.invoke('stripe-billing-checkout', {
    body: {
      op: 'add_dedicated_line',
      org_id: input.orgId,
      label: input.label ?? null,
      requested_area_code: input.requestedAreaCode ?? null,
      assigned_store_id: input.assignedStoreId ?? null,
    },
  });
  if (error) {
    // Functions client wraps non-2xx as FunctionsHttpError with `.context`
    // (a Response). The edge function returns { ok:false, error, reason }
    // — surface `reason` so the UI can branch on `trial_block`,
    // `subscription_not_active`, `stripe_payment_failed`, etc.
    type EdgeErrorShape = {
      error?: string;
      reason?: string;
    };
    let body: EdgeErrorShape | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ctx = (error as any).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        body = (await ctx.json()) as EdgeErrorShape;
      } catch {
        body = null;
      }
    }
    const msg = body?.error ?? error.message ?? 'Failed to add dedicated line.';
    const wrapped = new Error(body?.reason ? `${body.reason}: ${msg}` : msg);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (wrapped as any).reason = body?.reason ?? null;
    throw wrapped;
  }
  return data as AddDedicatedLineResult;
}

/**
 * Assign a managed line to a specific store (or unassign by passing null).
 * Backed by `core.rpc_sendblue_assign_line_to_store`. The DB enforces:
 *   - caller is org owner/admin,
 *   - target line belongs to the same org as the target store,
 *   - no other line is already pinned to that store (UNIQUE partial idx).
 */
export async function assignLineToStore(
  subaccountId: string,
  storeId: string | null,
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_sendblue_assign_line_to_store' as any, {
      p_subaccount_id: subaccountId,
      p_store_id: storeId,
    });
  if (error) throw error;
}

/**
 * Promote a managed line to be the org's default — i.e. the row the
 * 4-rung sender resolution ladder falls back to when a sending store has
 * no `assigned_store_id` match. Backed by `core.rpc_sendblue_set_default_line`
 * which atomically clears `is_default` on every other row in the same org.
 */
export async function setDefaultLine(subaccountId: string): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_sendblue_set_default_line' as any, { p_subaccount_id: subaccountId });
  if (error) throw error;
}

/**
 * Cancel a specific managed line by id. Used by the operator's "Cancel
 * line" action in Settings → Messaging and by the webhook reconciler
 * when the Stripe-side quantity drops below the live SendBlue row count.
 *
 * Marks the row `pending_cancel`; the cron-driven worker tears down the
 * SendBlue subaccount + vault secret on its next sweep.
 */
export async function cancelLineById(
  subaccountId: string,
  reason: SendblueCancelReason = 'operator_request',
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_sendblue_cancel_line_by_id' as any, {
      p_subaccount_id: subaccountId,
      p_reason: reason,
    });
  if (error) throw error;
}

/**
 * Returns the most recent N lifecycle events for the org's subaccount,
 * newest first. Useful for the "what happened?" timeline in the
 * settings UI. RLS mirrors `sendblue_subaccounts`.
 */
export async function listSendblueSubaccountEvents(
  orgId: string,
  limit = 25,
): Promise<SendblueSubaccountEvent[]> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('sendblue_subaccount_events' as any)
    .select('*')
    .eq('org_id', orgId)
    .order('at', { ascending: false })
    .limit(Math.max(1, Math.min(200, Math.floor(limit))));
  if (error) throw error;
  return (data ?? []) as unknown as SendblueSubaccountEvent[];
}

// ---- admin reads --------------------------------------------------------

/**
 * Platform-admin only: list every managed subaccount with org + plan
 * context. Backed by `core.rpc_admin_list_sendblue_subaccounts()` --
 * see migration 20260612030000_sendblue_admin_rpcs.sql.
 */
export async function adminListSendblueSubaccounts(): Promise<AdminSendblueSubaccountRow[]> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_admin_list_sendblue_subaccounts' as any);
  if (error) throw error;
  return (data ?? []) as unknown as AdminSendblueSubaccountRow[];
}

/**
 * Platform-admin only: load the full lifecycle event history for one
 * subaccount. Backed by
 * `core.rpc_admin_list_sendblue_subaccount_events(p_subaccount_id, p_limit)`.
 */
export async function adminListSendblueSubaccountEvents(
  subaccountId: string,
  limit = 100,
): Promise<SendblueSubaccountEvent[]> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_admin_list_sendblue_subaccount_events' as any, {
      p_subaccount_id: subaccountId,
      p_limit: Math.max(1, Math.min(500, Math.floor(limit))),
    });
  if (error) throw error;
  return (data ?? []) as unknown as SendblueSubaccountEvent[];
}

// ---- admin writes -------------------------------------------------------

/**
 * Re-queue a failed (or cancelled) subaccount for the worker. Idempotent
 * for live states. Backed by `core.rpc_sendblue_request_subaccount`.
 */
export async function adminRequeueSendblueSubaccount(
  orgId: string,
  requestedAreaCode?: string | null,
): Promise<string> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_sendblue_request_subaccount' as any, {
      p_org_id: orgId,
      p_requested_area_code: requestedAreaCode ?? null,
    });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Flip a managed subaccount to `pending_cancel` and disable the routing
 * connection immediately. The worker then ships the support email.
 * Backed by `core.rpc_sendblue_request_cancel`.
 */
export async function adminRequestSendblueCancel(
  orgId: string,
  reason: SendblueCancelReason,
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_sendblue_request_cancel' as any, {
      p_org_id: orgId,
      p_reason: reason,
    });
  if (error) throw error;
}

/**
 * Final state: operator confirms Sendblue support has actioned the
 * release. Deletes the vault secret + connection row and stamps
 * `cancelled_at`. Backed by `core.rpc_sendblue_mark_cancelled`.
 */
export async function adminMarkSendblueCancelled(orgId: string): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_sendblue_mark_cancelled' as any, { p_org_id: orgId });
  if (error) throw error;
}
